<template>
    <div>
        <!-- Add New Time Section -->
        <div class="bg-white shadow-2 q-pa-md rounded-borders q-mb-md">
            <div class="row q-col-gutter-md">
                <div class="col-12">
                    <h3>{{ labels.create_shift_time }}</h3>
                </div>
            </div>
            <div class="row q-col-gutter-md">
                <div class="col-12 col-md-4">
                    <label>{{ labels.weekday_label }}</label>
                    <q-select
                        v-model="newTime.day"
                        :options="formattedWeekdayOptions"
                        emit-value
                        map-options
                        outlined
                        dense
                        bg-color="grey-2"
                        class="q-mb-md"
                    />
                </div>

                <div class="col-12 col-md-4">
                    <label>{{ labels.start_label }}</label>
                    <q-select
                        v-model="newTime.start"
                        :options="formattedTimeOptions"
                        emit-value
                        map-options
                        outlined
                        dense
                        bg-color="grey-2"
                        class="q-mb-md"
                    />
                </div>

                <div class="col-12 col-md-4">
                    <label>{{ labels.end_label }}</label>
                    <q-select
                        v-model="newTime.end"
                        :options="formattedTimeOptions"
                        emit-value
                        map-options
                        outlined
                        dense
                        bg-color="grey-2"
                        class="q-mb-md"
                    />
                </div>

                <div class="col-12">
                    <label>{{ labels.additional_booking_info_multi }}</label>
                    <q-select
                        v-model="newTime.shiftNeedsItems"
                        :options="formattedBookableItemOptions"
                        multiple
                        emit-value
                        map-options
                        outlined
                        dense
                        bg-color="grey-2"
                        class="q-mb-md"
                        style="min-height: 100px"
                    />
                </div>

                <div class="col-12">
                    <label>{{ labels.shift_title }}</label>
                    <q-input
                        v-model="newTime.title"
                        :placeholder="labels.shift_title_hint"
                        outlined
                        dense
                        bg-color="grey-2"
                        class="q-mb-md"
                    />
                </div>

                <div class="col-12 col-md-6">
                    <label>{{ labels.shift_slot_count }}</label>
                    <q-input
                        v-model="newTime.places"
                        type="number"
                        outlined
                        dense
                        bg-color="grey-2"
                        class="q-mb-md"
                    />
                </div>

                <div class="col-12 col-md-6">
                    <label>{{ labels.overbooking_from }}</label>
                    <q-input
                        v-model="
                            newTime.showRedAtThisAmountOfAttendingPublishers
                        "
                        type="number"
                        outlined
                        dense
                        bg-color="grey-2"
                        class="q-mb-md"
                    />
                </div>

                <div class="col-12">
                    <label>{{ labels.specific_shift_description }}</label>
                    <q-input
                        v-model="newTime.description"
                        type="textarea"
                        :placeholder="labels.shift_description_hint"
                        outlined
                        dense
                        bg-color="grey-2"
                        class="q-mb-md"
                    />
                </div>

                <div class="col-12">
                    <q-btn
                        @click="addTime"
                        color="primary"
                        size="sm"
                        class="q-mt-lg full-width"
                        :label="labels.save_time"
                    />
                </div>
            </div>
        </div>

        <!-- List Section -->
        <div class="bg-white shadow-2 q-pa-md rounded-borders">
            <div class="row q-col-gutter-md">
                <div class="col-12">
                    <h3>{{ labels.weekly_plan }}</h3>
                    <p v-if="times.length === 0" id="emptyHint">
                        <i>{{ labels.no_times_yet }}</i>
                    </p>
                    <div id="plan">
                        <div class="row q-col-gutter-md">
                            <div
                                v-for="(time, index) in times"
                                :key="index"
                                class="col-12 col-md-4 col-lg-3 planItem q-pb-md"
                            >
                                <div
                                    class="bg-white shadow-2 q-pa-md rounded-borders text-center callout"
                                >
                                    <!-- Hidden Input for Form Submission -->
                                    <input
                                        type="hidden"
                                        name="times[]"
                                        :value="
                                            encodeURIComponent(
                                                JSON.stringify(time),
                                            )
                                        "
                                    />

                                    <div class="row q-col-gutter-md">
                                        <div class="col-12">
                                            <q-btn
                                                size="sm"
                                                color="negative"
                                                class="float-right"
                                                icon="close"
                                                @click="removeTime(index)"
                                            />
                                            <strong>{{
                                                getWeekdayLabel(time.day)
                                            }}</strong
                                            ><br />
                                            {{ time.start }} {{ labels.until }}
                                            {{ time.end }}
                                            {{ labels.clock_short }}
                                            <p
                                                class="text-caption"
                                                style="line-height: 1.2"
                                            >
                                                {{ labels.shift_title }}:
                                                {{ time.title || "-" }}<br />
                                                {{
                                                    labels.specific_shift_description
                                                }}: {{ time.description || "-"
                                                }}<br />
                                                {{ labels.shift_slot_count }}:
                                                {{ time.places || "-" }}<br />
                                                <span
                                                    v-if="
                                                        time.showRedAtThisAmountOfAttendingPublishers
                                                    "
                                                >
                                                    {{
                                                        labels.overbooking_from
                                                    }}:
                                                    {{
                                                        time.showRedAtThisAmountOfAttendingPublishers
                                                    }}<br />
                                                </span>
                                                {{ labels.booked_items }}:
                                                {{
                                                    time.shiftNeedsItemsStr ||
                                                    "-"
                                                }}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from "vue";

export default {
    name: "WeekplanEditor",
    props: {
        initialTimes: {
            type: Array,
            default: () => [],
        },
        weekdayOptions: {
            type: [Object, Array],
            default: () => ({}),
        },
        timeOptions: {
            type: [Object, Array],
            default: () => ({}),
        },
        bookableItemOptions: {
            type: [Object, Array],
            default: () => ({}),
        },
        labels: {
            type: Object,
            default: () => ({}),
        },
    },
    setup(props) {
        const times = ref(
            Array.isArray(props.initialTimes)
                ? [...props.initialTimes]
                : Object.values(props.initialTimes),
        );

        const newTime = reactive({
            day: null,
            start: null,
            end: null,
            title: "",
            description: "",
            places: "",
            showRedAtThisAmountOfAttendingPublishers: "",
            shiftNeedsItems: [],
        });

        const formatOptions = (options) => {
            if (Array.isArray(options)) {
                // Assuming array of strings or objects. If objects with label/value, fine.
                // If simple array, value=label.
                return options.map((opt) => {
                    if (typeof opt === "object") return opt;
                    return { label: opt, value: opt };
                });
            }
            // Object: key => value (label)
            return Object.keys(options).map((key) => ({
                label: options[key],
                value: key,
            }));
        };

        const formattedWeekdayOptions = computed(() =>
            formatOptions(props.weekdayOptions),
        );
        const formattedTimeOptions = computed(() =>
            formatOptions(props.timeOptions),
        );
        const formattedBookableItemOptions = computed(() =>
            formatOptions(props.bookableItemOptions),
        );

        const getWeekdayLabel = (dayValue) => {
            const option = formattedWeekdayOptions.value.find(
                (opt) => opt.value == dayValue,
            );
            return option ? option.label : dayValue;
        };

        const addTime = () => {
            // Basic validation
            if (!newTime.day || !newTime.start || !newTime.end) {
                // You might want to use Quasar dialog or toast here
                alert(
                    props.labels.pleaseAddTimesFirst ||
                        "Please fill required fields",
                );
                return;
            }

            // Calculate shiftNeedsItemsStr
            let shiftNeedsItemsStr = "";
            if (newTime.shiftNeedsItems && newTime.shiftNeedsItems.length > 0) {
                const selectedLabels = newTime.shiftNeedsItems.map((id) => {
                    const opt = formattedBookableItemOptions.value.find(
                        (o) => o.value == id,
                    );
                    return opt ? opt.label : id;
                });
                shiftNeedsItemsStr = selectedLabels.join(", ");
            }

            // Construct the time object matching the structure expected by backend/legacy JS
            const timeEntry = {
                day: newTime.day,
                start: newTime.start,
                end: newTime.end,
                title: newTime.title,
                description: newTime.description,
                places: newTime.places,
                showRedAtThisAmountOfAttendingPublishers:
                    newTime.showRedAtThisAmountOfAttendingPublishers,
                shiftNeedsItemIds: [...newTime.shiftNeedsItems], // Use spread to copy array
                shiftNeedsItemsStr: shiftNeedsItemsStr,
                // Legacy support: some views might use 'shiftNeedsItems' key for IDs too?
                // Weekplan.js used 'shiftNeedsItemIds'.
                // editweekplan.blade.php uses 'shiftNeedsItems' for values.
                // Let's include both to be safe?
                // But backend decodes JSON. Let's stick to what weekplan.js produced.
            };

            times.value.push(timeEntry);

            // Reset form
            newTime.title = "";
            newTime.description = "";
            // Keep day/start/end? Maybe user wants to add multiple for same day.
            // Usually better to reset or keep day. Let's keep day, maybe increment time?
            // For now, let's keep day, reset others to be helpful but not annoying.
            // Or just reset everything.
            // weekplan.js didn't reset anything! It just read values.
            // So keeping values might be better UX?
            // But typically "Add" implies clearing for next entry.
            // Let's reset times but keep day.
            // newTime.day = null; // Keep day
            newTime.start = null;
            newTime.end = null;
            newTime.places = "";
            newTime.showRedAtThisAmountOfAttendingPublishers = "";
            newTime.shiftNeedsItems = [];
        };

        const removeTime = (index) => {
            times.value.splice(index, 1);
        };

        onMounted(() => {
            const form = document.getElementById("form");
            if (form) {
                form.addEventListener("submit", (event) => {
                    // Allow delete action without validation
                    if (event.submitter && event.submitter.name === "delete") {
                        return;
                    }

                    if (times.value.length === 0) {
                        event.preventDefault();
                        event.stopImmediatePropagation();
                        alert(
                            props.labels.pleaseAddTimesFirst ||
                                "Please add times first",
                        );

                        // Re-enable buttons if disabled by other scripts (like app.js loading overlay)
                        if (event.submitter) {
                            setTimeout(() => {
                                event.submitter.disabled = false;
                                delete event.submitter.dataset
                                    .disabledByOverlay;
                            }, 100);
                        }

                        const overlay = document.getElementById(
                            "initial-loading-overlay",
                        );
                        if (overlay) {
                            overlay.style.display = "none";
                        }
                        return false;
                    }
                });
            }
        });

        return {
            times,
            newTime,
            formattedWeekdayOptions,
            formattedTimeOptions,
            formattedBookableItemOptions,
            addTime,
            removeTime,
            getWeekdayLabel,
            encodeURIComponent,
        };
    },
};
</script>
